import React, { useState, useEffect, useCallback, useMemo } from 'react';

const BINGO_COLORS = {
  B: '#3B82F6',
  I: '#EF4444',
  N: '#F59E0B',
  G: '#EC4899',
  O: '#8B5CF6',
};

const BINGO_LETTERS = ['B', 'I', 'N', 'G', 'O'];

// Convert column-based card data { B:[...], I:[...], N:[...], G:[...], O:[...] }
// into a 5x5 row-based grid for display
const columnsToGrid = (numbers) => {
  const cols = [numbers.B, numbers.I, numbers.N, numbers.G, numbers.O];
  const grid = [];
  for (let row = 0; row < 5; row++) {
    const rowData = [];
    for (let col = 0; col < 5; col++) {
      rowData.push(cols[col][row]);
    }
    grid.push(rowData);
  }
  return grid;
};

// Bingo number ranges per letter
const BINGO_RANGES = {
  B: Array.from({ length: 15 }, (_, i) => i + 1),
  I: Array.from({ length: 15 }, (_, i) => i + 16),
  N: Array.from({ length: 15 }, (_, i) => i + 31),
  G: Array.from({ length: 15 }, (_, i) => i + 46),
  O: Array.from({ length: 15 }, (_, i) => i + 61),
};

const BingoCardsGame = ({ cardNumbers, cardSet, onBack }) => {
  const [cards, setCards] = useState([]);
  const [markedNumbers, setMarkedNumbers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBoard, setShowBoard] = useState(true);

  // Load cards from the bundled per-set JSON file by palette number
  useEffect(() => {
    const loadCards = async () => {
      setLoading(true);
      setError('');

      if (cardNumbers.length === 0) {
        setError('No cards selected');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${process.env.PUBLIC_URL}/cards/${cardSet}.cards.json`);
        if (!res.ok) throw new Error('Failed to load cards data');
        const allCards = await res.json();

        const results = [];
        for (const num of cardNumbers) {
          const card = allCards.find(c => c.paletteNumber === num);
          if (!card) {
            setError(`Card #${num} not found`);
            setLoading(false);
            return;
          }
          results.push({
            cardNumber: card.paletteNumber,
            grid: columnsToGrid(card.numbers),
          });
        }

        setCards(results);
      } catch (err) {
        setError('Failed to load cards. Please check the numbers and try again.');
      }
      setLoading(false);
    };

    loadCards();
  }, [cardNumbers, cardSet]);

  // Toggle mark on a number (cross-card sync)
  const toggleMark = useCallback((number) => {
    if (number === 'FREE' || number === 0 || number === null) return;

    setMarkedNumbers(prev => {
      const next = new Set(prev);
      if (next.has(number)) {
        next.delete(number);
      } else {
        next.add(number);
      }
      return next;
    });
  }, []);

  // Reset all marks
  const handleReset = useCallback(() => {
    setMarkedNumbers(new Set());
  }, []);

  // Layout class based on card count
  const layoutClass = useMemo(() => {
    const count = cards.length;
    if (count === 1) return 'bc-game-layout-1';
    if (count === 2) return 'bc-game-layout-2';
    if (count === 3) return 'bc-game-layout-3';
    if (count === 4) return 'bc-game-layout-4';
    if (count === 5) return 'bc-game-layout-5';
    if (count === 6) return 'bc-game-layout-6';
    if (count === 7) return 'bc-game-layout-7';
    if (count === 8) return 'bc-game-layout-8';
    if (count === 9) return 'bc-game-layout-9';
    if (count === 10) return 'bc-game-layout-10';
    if (count === 11) return 'bc-game-layout-11';
    if (count >= 12) return 'bc-game-layout-12';
    return 'bc-game-layout-1';
  }, [cards.length]);

  if (loading) {
    return (
      <div className="bc-game">
        <div className="bc-game-loading">
          <span className="bc-spinner large" />
          <p>Loading your cards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bc-game">
        <div className="bc-game-error">
          <p>{error}</p>
          <button className="bc-auth-btn primary" onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bc-game">
      {/* Header */}
      <div className="bc-game-header">
        <button className="bc-back-btn" onClick={onBack}>
          &#8592;
        </button>
        <button
          className={`bc-board-toggle ${showBoard ? 'active' : ''}`}
          onClick={() => setShowBoard(prev => !prev)}
        >
          {showBoard ? 'Hide Board' : 'Board'}
        </button>
        <h2 className="bc-game-title">My Bingo Cards</h2>
        <button className="bc-reset-btn" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* Caller Number Board */}
      {showBoard && (
        <div className="bc-caller-board">
          {BINGO_LETTERS.map((letter) => (
            <div key={letter} className="bc-caller-row">
              <div
                className="bc-caller-letter"
                style={{ background: BINGO_COLORS[letter] }}
              >
                {letter}
              </div>
              <div className="bc-caller-numbers">
                {BINGO_RANGES[letter].map((num) => (
                  <button
                    key={num}
                    className={`bc-caller-num ${markedNumbers.has(num) ? 'called' : ''}`}
                    style={markedNumbers.has(num) ? { background: BINGO_COLORS[letter] } : undefined}
                    onClick={() => toggleMark(num)}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cards Container */}
      <div className={`bc-game-cards ${layoutClass}`}>
        {cards.map((card) => (
          <div key={card.cardNumber} className="bc-bingo-card">
            <div className="bc-bingo-card-number">Card {card.cardNumber}</div>

            {/* BINGO Header */}
            <div className="bc-bingo-header">
              {BINGO_LETTERS.map((letter) => (
                <div
                  key={letter}
                  className="bc-bingo-header-cell"
                  style={{ background: BINGO_COLORS[letter] }}
                >
                  {letter}
                </div>
              ))}
            </div>

            {/* 5x5 Grid */}
            <div className="bc-bingo-grid">
              {card.grid.map((row, rowIdx) => (
                row.map((cell, colIdx) => {
                  const isFree = cell === 'FREE';
                  const isMarked = isFree || markedNumbers.has(cell);
                  const cellValue = isFree ? 'FREE' : cell;

                  return (
                    <button
                      key={`${rowIdx}-${colIdx}`}
                      className={`bc-bingo-cell ${isMarked ? 'marked' : ''} ${isFree ? 'free' : ''}`}
                      onClick={() => !isFree && toggleMark(cell)}
                    >
                      <span className="bc-bingo-cell-number">{cellValue}</span>
                      {isFree && (
                        <span className="bc-bingo-cell-star">&#9733;</span>
                      )}
                    </button>
                  );
                })
              ))}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default BingoCardsGame;
